import { DeleteOutlineRounded, EditOutlined } from "@mui/icons-material";
import { Box, Button, Chip, Divider, Link, Skeleton, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { AvatarGroup, EditRoleModal, LinkBehavior, RoleFeed, ShareButton } from "../../components";
import { Links, SearchFilter } from "../../constants";
import { useSession } from "../../hooks";
import { useDeleteRole, useGetRole, useGetTeam } from "../../http";
import { setTitlePrefix, useDestructiveConfirm } from "../../utilities";
import { DetailPageLayout } from "./DetailPageLayout";

interface UrlParams {
  roleGuid: string;
}

const RoleDetailsPage = () => {
  const { roleGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { userId } = useSession();
  const navigate = useNavigate();
  const { data: role, isLoading: isLoadingRole } = useGetRole(roleGuid);
  const { data: team } = useGetTeam(role?.parentTeam.slug, userId);
  const { mutate: deleteRole } = useDeleteRole();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useDestructiveConfirm();

  useEffect(() => {
    setTitlePrefix(role?.name);
  }, [role?.name]);

  async function handleDeleteRoleClicked() {
    const { confirmed } = await confirm({
      title: "Delete Role",
      content: "Are you sure you want to delete this role?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteRole(
      {
        teamSlug: team!.slug,
        guid: roleGuid,
        modifiedByUserId: userId!,
      },
      {
        onSuccess: () => {
          if (role !== undefined) {
            navigate(Links.Team(role.parentTeam.slug));
            enqueueSnackbar(`Role "${role.name}" has been deleted`);
          }
        },
      },
    );
  }

  return (
    <DetailPageLayout
      title="Role"
      actions={
        <Stack direction="row" spacing={1}>
          <ShareButton variant="outlined" href={window.location.href} />
          {(team?.isMember || team?.isAdministrator) && (
            <Button
              variant="outlined"
              startIcon={<EditOutlined />}
              onClick={() => {
                setIsModalVisible(true);
              }}
            >
              Edit
            </Button>
          )}
          <Divider orientation="vertical" />
          {(team?.isMember || team?.isAdministrator) && (
            <Button variant="outlined" color="error" onClick={handleDeleteRoleClicked} sx={{ minWidth: 18 }}>
              <DeleteOutlineRounded />
            </Button>
          )}
        </Stack>
      }
    >
      {isLoadingRole && (
        <Stack spacing={0.5} sx={{ px: { xs: 0.5, sm: 1 }, my: { xs: 0.5, sm: 1 } }}>
          <Skeleton variant="rectangular" height={24} width="10%" />
          <Skeleton variant="rectangular" height={30} width="50%" />
        </Stack>
      )}

      {!isLoadingRole && role && (
        <>
          <Stack spacing={{ xs: 0.5, sm: 1 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <Stack spacing={{ xs: 0.5, sm: 1 }} sx={{ px: { xs: 0.5, sm: 1 } }}>
              <Stack spacing={0.5}>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    flexWrap: "wrap",
                  }}
                >
                  {role.parentTeam && (
                    <Chip
                      color="primary"
                      label={role.parentTeam.name}
                      component={LinkBehavior}
                      href={Links.Team(role.parentTeam.slug)}
                      clickable
                    />
                  )}

                  {role.isOpen && (
                    <Chip
                      label={`OPEN as of ${role.isOpenLastModifiedUtc
                        .toLocal()
                        .toLocaleString(DateTime.DATE_MED)
                        .toLocaleUpperCase()}`}
                      color="role"
                      variant="outlined"
                      sx={{
                        ".MuiChip-label": {
                          textTransform: "none",
                        },
                      }}
                    />
                  )}
                </Stack>

                <Typography variant="h3" sx={{ whiteSpace: "pre-line" }}>
                  {role.name}
                </Typography>
                <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                  {role.description}
                </Typography>
              </Stack>

              <Box>
                <Typography variant="overline">Accountabilities</Typography>
                {role.accountabilities && role.accountabilities.length > 0 ? (
                  <Stack sx={{ pl: 1, mt: 0.5 }}>
                    {role.accountabilities.map((accountability, index) => (
                      <Typography
                        key={index}
                        sx={{
                          display: "list-item",
                        }}
                      >
                        {accountability.description}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="placeholder" sx={{ mt: 0.5 }}>
                    No accountabilities defined.
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="overline">Tags</Typography>
                {role.tags && role.tags.length > 0 ? (
                  <Stack direction="row" divider={<Typography>,&nbsp;</Typography>} sx={{ mt: 0.5 }}>
                    {role.tags.map((tag, i) => (
                      <Link key={i} href={Links.Search(tag, SearchFilter.Roles)}>
                        {tag}
                      </Link>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="placeholder" sx={{ mt: 0.5 }}>
                    No tags defined.
                  </Typography>
                )}
              </Box>

              {role.members.length > 0 && (
                <Box>
                  <Typography variant="overline">People</Typography>
                  <AvatarGroup users={role.members} max={10} sx={{ mt: 0.75 }} />
                </Box>
              )}
            </Stack>
            <Box>
              <Divider />
              <RoleFeed roleGuid={roleGuid} />
            </Box>
          </Stack>

          <EditRoleModal
            visible={isModalVisible}
            role={role}
            onUpdated={() => setIsModalVisible(false)}
            onCancelled={() => setIsModalVisible(false)}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export { RoleDetailsPage };
